import { create } from 'zustand';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';

type SettingsState = {
  masterPassword: string | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  fetchMasterPassword: () => Promise<string>;
  updateMasterPassword: (newPassword: string) => Promise<void>;
};

// Default master password if none is set in Firestore
const DEFAULT_MASTER_PASSWORD = 'admin';

export const useSettingsStore = create<SettingsState>((set, get) => ({
  masterPassword: null,
  loading: false,
  error: null,
  initialized: false,

  fetchMasterPassword: async () => {
    set({ loading: true, error: null });
    try {
      // Get the master password document
      const passwordDocRef = doc(firestore, 'MasterPassword', 'password');
      const passwordDoc = await getDoc(passwordDocRef);
      
      let password: string;
      
      // If document exists, use the password from it
      if (passwordDoc.exists()) {
        password = passwordDoc.data().password || DEFAULT_MASTER_PASSWORD;
      } else {
        // If document doesn't exist, create it with the default password
        password = DEFAULT_MASTER_PASSWORD;
        await setDoc(passwordDocRef, { password: DEFAULT_MASTER_PASSWORD });
      }
      
      set({ masterPassword: password, loading: false, initialized: true });
      return password;
    } catch (error: any) {
      console.error('Error fetching master password:', error);
      set({ error: error.message, loading: false });
      // Return default password as fallback
      return DEFAULT_MASTER_PASSWORD;
    }
  },

  updateMasterPassword: async (newPassword: string) => {
    if (!newPassword.trim()) {
      throw new Error('Password cannot be empty');
    }
    
    set({ loading: true, error: null });
    try {
      // Update the master password document
      const passwordDocRef = doc(firestore, 'MasterPassword', 'password');
      await setDoc(passwordDocRef, { 
        password: newPassword,
        updatedAt: Date.now(),
        updatedBy: localStorage.getItem('userId') || 'unknown'
      });
      
      set({ masterPassword: newPassword, loading: false });
    } catch (error: any) {
      console.error('Error updating master password:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  }
}));